'use client';

/**
 * Catalog Page Client Component
 *
 * Header matches the marketing design (Image #10): "Edem AI" gradient title +
 * glowing-border dropdown filters (Gender / Style / Age), a search pill, and a
 * scrollable row of category pills. Grid is unchanged.
 */
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Filter,
  Flame,
  Search,
  Sparkles,
  X,
} from 'lucide-react';

import { CharacterCard } from '~/components/characters';
import { withPromoCards } from '~/components/characters/promo-grid';
import { featuredRank } from '~/lib/featured-characters';
import {
  type CharacterListItem,
  getPublicCharacters,
} from '~/lib/server/actions/character-actions';

type SortBy = 'newest' | 'popular' | 'name';
type StyleFilter = 'all' | 'realistic' | 'anime' | 'furry';
type GenderFilter = 'all' | 'female' | 'male' | 'trans';
type OpenMenu = 'gender' | 'style' | 'age' | null;

interface CatalogPageClientProps {
  initialCharacters: CharacterListItem[];
  initialTags: string[];
}

const GENDER_OPTIONS = [
  { value: 'female' as const, label: 'Female' },
  { value: 'male' as const, label: 'Male' },
  { value: 'trans' as const, label: 'Trans' },
];
const STYLE_OPTIONS = [
  { value: 'realistic' as const, label: 'Realistic' },
  { value: 'anime' as const, label: 'Anime' },
  { value: 'furry' as const, label: 'Furry' },
];
const AGE_OPTIONS = ['18-21', '22-30', '30-35', '35+'] as const;

const CATEGORIES: { key: string; label: string; Icon?: React.ElementType }[] = [
  { key: 'all', label: 'All', Icon: Sparkles },
  { key: 'popular', label: 'Popular', Icon: Flame },
  { key: 'new', label: 'New', Icon: Sparkles },
  { key: 'milf', label: 'MILF' },
  { key: 'cougar', label: 'Cougar' },
  { key: 'hotwife', label: 'Hotwife' },
  { key: 'voluptuous', label: 'Voluptuous' },
  { key: 'busty', label: 'Busty' },
  { key: 'curvy', label: 'Curvy' },
  { key: 'thick', label: 'Thick' },
  { key: 'stacked', label: 'Stacked' },
  { key: 'succubus', label: 'Succubus' },
  { key: 'nymph', label: 'Nymph' },
  { key: 'vixen', label: 'Vixen' },
  { key: 'seductress', label: 'Seductress' },
  { key: 'temptress', label: 'Temptress' },
  { key: 'bombshell', label: 'Bombshell' },
  { key: 'goddess', label: 'Goddess' },
];

// Category keys that map to built-in filters (sort/style/status). Every other
// key in CATEGORIES is a tag keyword and filters against the character's tags.
const NON_TAG_CATEGORIES = new Set([
  'all',
  'popular',
  'new',
  'trending',
  'realistic',
  'photorealistic',
  'anime',
  'furry',
]);

// The category pills are marketing keywords that almost never exist verbatim
// in `tags` (real tags are things like "japanese", "athletic", "confident").
// Matching tags alone therefore returned zero characters for every pill. Each
// pill instead matches the real attribute columns (body_type, chest_size,
// age); tag/description keywords still count so admins can hand-tag a
// character into any category.
const bodyIncludes = (c: CharacterListItem, ...parts: string[]) => {
  const bodyType = (c.bodyType ?? '').toLowerCase();
  return parts.some((p) => bodyType.includes(p));
};
const chestIn = (c: CharacterListItem, ...sizes: string[]) =>
  sizes.includes((c.chestSize ?? '').toLowerCase());
const hasKeyword = (c: CharacterListItem, keywords: string[]) => {
  const haystack = [...(c.tags ?? []), c.description ?? '', c.name]
    .join(' ')
    .toLowerCase();
  return keywords.some((k) => haystack.includes(k));
};

const CATEGORY_MATCHERS: Record<string, (c: CharacterListItem) => boolean> = {
  milf: (c) => c.gender === 'female' && (c.age ?? 0) >= 30,
  cougar: (c) => c.gender === 'female' && (c.age ?? 0) >= 38,
  hotwife: (c) =>
    c.gender === 'female' && (c.age ?? 0) >= 27 && (c.age ?? 0) <= 40,
  voluptuous: (c) => bodyIncludes(c, 'voluptuous', 'full-figured'),
  busty: (c) => chestIn(c, 'large', 'huge') || bodyIncludes(c, 'voluptuous'),
  curvy: (c) => bodyIncludes(c, 'curvy', 'voluptuous'),
  thick: (c) => bodyIncludes(c, 'curvy', 'voluptuous') || chestIn(c, 'huge'),
  stacked: (c) =>
    chestIn(c, 'huge') ||
    (chestIn(c, 'large') && bodyIncludes(c, 'curvy', 'voluptuous')),
  succubus: (c) =>
    hasKeyword(c, ['succubus', 'demon', 'devil', 'fantasy', 'intense']),
  nymph: (c) =>
    hasKeyword(c, ['nymph', 'playful', 'flirty', 'spiritual', 'free-spirit']),
  vixen: (c) => hasKeyword(c, ['vixen', 'seductive', 'confident', 'fox']),
  seductress: (c) =>
    hasKeyword(c, ['seductress', 'seductive', 'passionate', 'sensual']),
  temptress: (c) =>
    hasKeyword(c, ['temptress', 'seductive', 'intense', 'sensual']),
  bombshell: (c) =>
    hasKeyword(c, ['bombshell', 'glamorous', 'stunning', 'elegant']) ||
    (chestIn(c, 'large', 'huge') && bodyIncludes(c, 'curvy', 'voluptuous')),
  goddess: (c) =>
    hasKeyword(c, ['goddess', 'divine', 'elegant', 'powerful', 'spiritual']),
};

/** Venus / Mars / Transgender glyph for the gender pill. */
function GenderGlyph({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };
  if (gender === 'male') {
    return (
      <svg {...common}>
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  if (gender === 'trans') {
    return (
      <svg {...common}>
        <circle cx="11" cy="12" r="5" />
        <line x1="11" y1="17" x2="11" y2="22" />
        <line x1="8" y1="20" x2="14" y2="20" />
        <line x1="19" y1="2" x2="13.5" y2="7.5" />
        <polyline points="14 2 19 2 19 7" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="9" r="6" />
      <line x1="12" y1="15" x2="12" y2="22" />
      <line x1="9" y1="19" x2="15" y2="19" />
    </svg>
  );
}

// ── Glowing-border pill styles (inline so they don't depend on Tailwind JIT) ──
const glowWrap = (active: boolean): CSSProperties => ({
  display: 'inline-flex',
  borderRadius: '9999px',
  padding: '1.5px',
  background: active
    ? 'linear-gradient(90deg, #ec4899, #a855f7)'
    : 'linear-gradient(90deg, rgba(236,72,153,0.55), rgba(168,85,247,0.55))',
  boxShadow: active
    ? '0 0 16px rgba(168,85,247,0.45)'
    : '0 0 11px rgba(168,85,247,0.28)',
});
// Fluid sizing — pills scale smoothly with the viewport instead of jumping at
// breakpoints, so they stay proportionate on laptops and shrink on mobile.
const innerPill = (active: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'clamp(6px, 0.5vw, 8px)',
  borderRadius: '9999px',
  padding: 'clamp(6px, 0.6vw, 9px) clamp(12px, 1.1vw, 18px)',
  fontSize: 'clamp(12.5px, 0.9vw, 15px)',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
  background: active ? 'transparent' : '#0c0c12',
});
const menuStyle = (open: boolean): CSSProperties => ({
  backgroundColor: '#1a1330',
  minWidth: '170px',
  opacity: open ? 1 : 0,
  transform: open ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)',
  transformOrigin: 'top center',
  pointerEvents: open ? 'auto' : 'none',
  transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.16,1,0.3,1)',
});
const optClass = (selected: boolean) =>
  `flex w-full items-center rounded-xl px-3 py-2 text-left text-sm transition-colors ${
    selected
      ? 'bg-white/10 text-white'
      : 'text-gray-300 hover:bg-white/5 hover:text-white'
  }`;

function Dropdown({
  icon,
  label,
  active,
  open,
  onToggle,
  children,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="relative z-30">
      <span style={glowWrap(active)}>
        <button type="button" onClick={onToggle} style={innerPill(active)}>
          {icon}
          {label}
          <ChevronDown
            className="h-4 w-4"
            style={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </button>
      </span>
      <div
        className="absolute top-full left-0 z-50 mt-2 overflow-hidden rounded-2xl border border-white/10 p-1.5 shadow-2xl"
        style={menuStyle(open)}
        aria-hidden={!open}
      >
        {children}
      </div>
    </div>
  );
}

export function CatalogPageClient({
  initialCharacters,
}: CatalogPageClientProps) {
  const router = useRouter();
  const [characters, setCharacters] =
    useState<CharacterListItem[]>(initialCharacters);

  // The server ships only the first 48 characters (serializing all 1000 put
  // ~350KB of flight data in every HTML response). Top the list up after
  // hydration, once the initial paint has had the bandwidth to itself, so
  // filters and "Load more" still operate on the full catalog.
  useEffect(() => {
    if (initialCharacters.length < 48) return; // already the full set

    const timer = setTimeout(() => {
      getPublicCharacters(1000, 0)
        .then(({ characters: all }) => {
          if (all.length > initialCharacters.length) setCharacters(all);
        })
        .catch(() => {
          // The first 48 remain browsable; a failed top-up is not an error state.
        });
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [styleFilter, setStyleFilter] = useState<StyleFilter>('all');
  const [ageFilter, setAgeFilter] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'new'>('all');
  const [sortBy, setSortBy] = useState<SortBy>('newest');
  const [activeCategory, setActiveCategory] = useState('all');
  const [nameSearch, setNameSearch] = useState('');
  const [displayCount, setDisplayCount] = useState(20);
  const [open, setOpen] = useState<OpenMenu>(null);

  // Read ?q= param from URL on mount. window.location instead of
  // useSearchParams: this page prerenders statically, and useSearchParams
  // would CSR-bail the whole grid out of the static HTML.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) {
      setNameSearch(q);
      router.replace('/catalog', { scroll: false });
    }
  }, [router]);

  const selectCategory = (key: string) => {
    setActiveCategory(key);
    switch (key) {
      case 'all':
        setStyleFilter('all');
        setFilterStatus('all');
        setSortBy('newest');
        break;
      case 'popular':
      case 'trending':
        setSortBy('popular');
        break;
      case 'new':
        setFilterStatus('new');
        break;
      case 'realistic':
      case 'photorealistic':
        setStyleFilter('realistic');
        break;
      case 'anime':
        setStyleFilter('anime');
        break;
      case 'furry':
        setStyleFilter('furry');
        break;
      default:
        // Keyword/tag pill: clear the built-in filters so the tag is the primary
        // filter. The actual tag match happens in allFiltered against char.tags.
        setStyleFilter('all');
        setFilterStatus('all');
        setSortBy('newest');
        break;
    }
  };

  const allFiltered = characters
    .filter((char) => {
      const matchesStatus =
        filterStatus === 'all' || (filterStatus === 'new' && char.isNew);
      const matchesStyle =
        styleFilter === 'all' || (char.style ?? 'realistic') === styleFilter;
      const matchesGender =
        genderFilter === 'all' || char.gender === genderFilter;
      // Numeric age-range match (mirrors the landing page). The previous
      // implementation compared the range string against tags, which never
      // matched — selecting an age emptied the grid.
      const matchesAge =
        !ageFilter ||
        (char.age != null &&
          (ageFilter === '18-21'
            ? char.age >= 18 && char.age <= 21
            : ageFilter === '22-30'
              ? char.age >= 22 && char.age <= 30
              : ageFilter === '30-35'
                ? char.age >= 30 && char.age <= 35
                : char.age >= 35));
      const matchesName =
        nameSearch.trim() === '' ||
        char.name.toLowerCase().includes(nameSearch.trim().toLowerCase());
      const matchesCategory =
        NON_TAG_CATEGORIES.has(activeCategory) ||
        (char.tags ?? []).some(
          (t) => t.toLowerCase() === activeCategory.toLowerCase(),
        ) ||
        (CATEGORY_MATCHERS[activeCategory]?.(char) ?? false);
      return (
        matchesStatus &&
        matchesStyle &&
        matchesGender &&
        matchesAge &&
        matchesName &&
        matchesCategory
      );
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'popular')
        return (b.conversationCount ?? 0) - (a.conversationCount ?? 0);
      // 'newest' (default) — featured characters pinned first, then newest.
      const fr = featuredRank(a.slug) - featuredRank(b.slug);
      if (fr !== 0) return fr;
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });

  const filteredCharacters = allFiltered.slice(0, displayCount);
  const hasMore = displayCount < allFiltered.length;

  const clearAll = () => {
    setGenderFilter('all');
    setStyleFilter('all');
    setAgeFilter(null);
    setFilterStatus('all');
    setActiveCategory('all');
    setNameSearch('');
  };

  const genderLabel =
    GENDER_OPTIONS.find((o) => o.value === genderFilter)?.label ?? 'Gender';
  const styleLabel =
    STYLE_OPTIONS.find((o) => o.value === styleFilter)?.label ?? 'Style';

  return (
    <div
      className="min-h-screen flex-1"
      style={{
        background:
          'linear-gradient(to bottom right, #0a0a0a, #160a2a, #0d001a)',
      }}
    >
      {/* Page Header */}
      <section className="pt-5 pb-4 sm:pt-8 sm:pb-6">
        <div className="mx-auto px-3 sm:px-4" style={{ maxWidth: '1400px' }}>
          {/* Click-away backdrop for open dropdowns */}
          {open && (
            <div
              className="fixed inset-0 z-20"
              aria-hidden
              onClick={() => setOpen(null)}
            />
          )}

          {/* Top row: title + dropdown filters */}
          <div className="relative z-30 mb-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl md:text-4xl">
              <span
                style={{
                  background: 'linear-gradient(90deg, #ff9ed2, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Edem AI
              </span>{' '}
              <span className="text-white">Catalog</span>
            </h1>

            <div className="flex flex-wrap gap-2.5">
              {/* Gender */}
              <Dropdown
                icon={
                  <GenderGlyph
                    gender={genderFilter}
                    className="h-4 w-4 text-white"
                  />
                }
                label={genderLabel}
                active={genderFilter !== 'all'}
                open={open === 'gender'}
                onToggle={() => setOpen(open === 'gender' ? null : 'gender')}
              >
                <button
                  className={optClass(genderFilter === 'all')}
                  onClick={() => {
                    setGenderFilter('all');
                    setOpen(null);
                  }}
                >
                  All genders
                </button>
                {GENDER_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    className={optClass(genderFilter === o.value)}
                    onClick={() => {
                      setGenderFilter(o.value);
                      setOpen(null);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </Dropdown>

              {/* Style */}
              <Dropdown
                icon={<Sparkles className="h-4 w-4 text-white" />}
                label={styleLabel}
                active={styleFilter !== 'all'}
                open={open === 'style'}
                onToggle={() => setOpen(open === 'style' ? null : 'style')}
              >
                <button
                  className={optClass(styleFilter === 'all')}
                  onClick={() => {
                    setStyleFilter('all');
                    setOpen(null);
                  }}
                >
                  All styles
                </button>
                {STYLE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    className={optClass(styleFilter === o.value)}
                    onClick={() => {
                      setStyleFilter(o.value);
                      setOpen(null);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </Dropdown>

              {/* Age */}
              <Dropdown
                icon={<Calendar className="text-eros-purple h-4 w-4" />}
                label={ageFilter ?? 'Age'}
                active={!!ageFilter}
                open={open === 'age'}
                onToggle={() => setOpen(open === 'age' ? null : 'age')}
              >
                <button
                  className={optClass(!ageFilter)}
                  onClick={() => {
                    setAgeFilter(null);
                    setOpen(null);
                  }}
                >
                  Any age
                </button>
                {AGE_OPTIONS.map((a) => (
                  <button
                    key={a}
                    className={optClass(a === ageFilter)}
                    onClick={() => {
                      setAgeFilter(a);
                      setOpen(null);
                    }}
                  >
                    {a}
                  </button>
                ))}
              </Dropdown>
            </div>
          </div>

          {/* Second row: search + category pills */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative w-[180px] flex-shrink-0 sm:w-[240px]">
              <Search
                className="text-gray-400"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '16px',
                  height: '16px',
                }}
              />
              <input
                type="text"
                placeholder="Search name..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                style={{ paddingLeft: '42px', paddingRight: '30px' }}
                className="focus:border-eros-pink/50 w-full rounded-full border border-white/10 bg-white/[0.06] py-2.5 text-sm text-white placeholder-gray-500 transition-all focus:outline-none"
              />
              {nameSearch && (
                <button
                  onClick={() => setNameSearch('')}
                  className="text-gray-400 transition-colors hover:text-white"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <X style={{ width: '14px', height: '14px' }} />
                </button>
              )}
            </div>

            {/* Category pills (scrollable) */}
            <div className="scrollbar-none flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1">
              {CATEGORIES.map((cat) => {
                const isActive = activeCategory === cat.key;
                return (
                  <span
                    key={cat.key}
                    style={glowWrap(isActive)}
                    className="flex-shrink-0"
                  >
                    <button
                      type="button"
                      onClick={() => selectCategory(cat.key)}
                      style={{
                        ...innerPill(isActive),
                        padding:
                          'clamp(6px, 0.5vw, 8px) clamp(11px, 1vw, 16px)',
                      }}
                    >
                      {cat.label}
                    </button>
                  </span>
                );
              })}
            </div>

            {/* Scroll affordance */}
            <span
              style={glowWrap(false)}
              className="hidden flex-shrink-0 sm:inline-flex"
            >
              <span
                style={{
                  ...innerPill(false),
                  padding: '8px',
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* Characters Grid */}
      <section className="min-h-[60vh] py-3 sm:py-8">
        <div className="mx-auto px-3 sm:px-4" style={{ maxWidth: '1400px' }}>
          {characters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="from-eros-pink/20 to-eros-purple/20 mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br">
                <Sparkles className="text-eros-pink h-8 w-8" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-white">
                No characters yet
              </h3>
              <p className="mb-5 max-w-xs text-sm text-gray-400">
                Be the first to create one.
              </p>
              <Link
                href="/create"
                rel="nofollow"
                className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                Create a character
              </Link>
            </div>
          ) : filteredCharacters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.04]">
                <Filter className="h-7 w-7 text-gray-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-white">
                No matches
              </h3>
              <p className="mb-5 text-sm text-gray-500">
                Try adjusting your filters.
              </p>
              <button
                onClick={clearAll}
                className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 lg:gap-6 xl:grid-cols-4">
                {withPromoCards(
                  filteredCharacters.map((character, index) => (
                    <CharacterCard
                      key={character.id}
                      id={character.id}
                      slug={character.slug}
                      name={character.name}
                      age={character.age ?? undefined}
                      ethnicity={character.ethnicity}
                      image={
                        character.avatarUrl ||
                        `https://placehold.co/300x400/a855f7/ffffff?text=${encodeURIComponent(character.name)}`
                      }
                      description={character.description || undefined}
                      status={character.isNew ? 'NEW' : null}
                      tags={character.tags || undefined}
                      priority={index < 8}
                    />
                  )),
                )}
              </div>

              {hasMore && (
                <div className="mt-8 text-center sm:mt-12">
                  <button
                    onClick={() => setDisplayCount((prev) => prev + 24)}
                    className="border-eros-pink text-eros-pink hover:bg-eros-pink rounded-xl border px-6 py-2.5 text-sm font-semibold transition-colors hover:text-white"
                  >
                    Load more ({allFiltered.length - displayCount})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
